import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { assetUrl } from "@/lib/api";
import { trackEngagement } from "@/lib/tracking/tracker";

const PLAY_EVENT = "tn-video-play";

export const VideoTestimonial = ({ item, index }) => {
  const { t } = useSite();
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  // Only one testimonial plays at a time.
  useEffect(() => {
    const onOther = (e) => { if (e.detail !== ref.current) ref.current?.pause(); };
    window.addEventListener(PLAY_EVENT, onOther);
    return () => window.removeEventListener(PLAY_EVENT, onOther);
  }, []);

  const play = () => {
    const v = ref.current;
    if (!v) return;
    window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: v }));
    v.play().catch(() => {});
    setPlaying(true);
    trackEngagement("testimonial_video_play", { testimonial_index: index, testimonial_name: item.name });
  };

  return (
    <figure data-testid={`testimonial-card-${index}`} className="group relative overflow-hidden rounded-[28px] bg-forest-deep shadow-[0_30px_60px_-30px_rgba(13,59,46,0.6)]">
      <div className="relative aspect-[9/16]">
        <video
          ref={ref}
          data-testid={`testimonial-video-${index}`}
          src={assetUrl(item.video)}
          poster={assetUrl(item.poster) || undefined}
          preload="none"
          playsInline
          controls={playing}
          onEnded={() => setPlaying(false)}
          className="h-full w-full object-cover"
        />
        {!playing && (
          <button type="button" onClick={play} data-testid={`testimonial-play-${index}`} aria-label={`${t("play_video")}: ${item.name}`}
            className="absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-black/85 via-black/10 to-transparent p-5 text-left">
            <span className="self-end rounded-full bg-black/40 px-3 py-1 text-sm font-bold text-white backdrop-blur">0{index + 1}</span>
            <span className="grid h-16 w-16 place-items-center self-center rounded-full bg-gold text-forest shadow-xl transition-transform duration-500 group-hover:scale-110">
              <Play className="ml-1 h-7 w-7 fill-current" aria-hidden />
            </span>
            <figcaption>
              <span className="block text-xl font-extrabold text-white">{item.name}</span>
              {item.location && <span className="text-[15px] font-medium text-white/90">{item.location}</span>}
            </figcaption>
          </button>
        )}
      </div>
      {item.text && <blockquote className="bg-white p-5 text-[15px] leading-relaxed text-ink">{item.text}</blockquote>}
    </figure>
  );
};
